import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useMemo } from 'react';
import { attemptsOn, doneKindsOn, doneTitleOn, usedLineages } from '../content/sessions';
import {
  EMPTY_WORKBOOK,
  nextUnused,
  type Passage,
  type Prompt,
  type Sheet,
} from '../content/workbook';
import { localDayKey } from '../domain/day';
import {
  type DailyPlan,
  dailyPlanSchema,
  EXERCISE_ORDER,
  type ExerciseKind,
} from '../domain/exercise';
import { EXERCISE_COPY } from '../domain/exercise-copy';
import { useExerciseMinutes } from './exercise-minutes-store';
import { useExerciseProgress } from './exercise-progress-store';
import { useConfig } from './provider';
import { useLocalSessions } from './use-local-sessions';
import { useCountedSessions } from './use-marks';
import { useWorkbook } from './use-workbook';

/** Today as YYYY-MM-DD in the participant's local calendar — never the UTC date. */
export function todayIso(now = new Date()): string {
  return localDayKey(now);
}

// Bound to consts so the placeholders are named (`{stage}`, `{sheet}`), per docs/i18n.md.
const stage = 'stage';
const sheet = 'sheet';
const MATH_SUBTITLE = msg`Stage ${stage} · Sheet ${sheet}`;

export interface NextMaterial {
  sheet: Sheet | undefined;
  prompt: Prompt | undefined;
  /** The next passage to read aloud; undefined where none are provisioned. */
  passage: Passage | undefined;
  /**
   * Whether the answer is known: the workbook and the sessions have loaded (or cannot —
   * no types in this environment, or a failed fetch). Until then the three above are
   * undefined for a reason a screen must not mistake for "nothing provisioned", or it shows
   * its fallback first and swaps it for the real material a moment later.
   */
  ready: boolean;
}

/**
 * What the routine offers next, derived from the workbook and what was already used —
 * never a stored cursor (docs/mvp/03-CONTENT.md › "Pick for me").
 */
export function useNextMaterial(): NextMaterial {
  const workbook = useWorkbook();
  const sessions = useLocalSessions();
  const { strongerMemoryTypes } = useConfig();
  const ready =
    strongerMemoryTypes === undefined ||
    ((workbook.data !== undefined || workbook.isError) &&
      (sessions.data !== undefined || sessions.isError));
  return useMemo(() => {
    const book = workbook.data ?? EMPTY_WORKBOOK;
    const done = sessions.data ?? [];
    const sheet = nextUnused(book.sheets, usedLineages(done, 'math'));
    const prompt = nextUnused(book.prompts, usedLineages(done, 'writing'));
    const passage = nextUnused(book.passages, usedLineages(done, 'reading'));
    return {
      sheet,
      prompt,
      passage,
      ready,
    };
  }, [workbook.data, sessions.data, ready]);
}

/**
 * Today's routine — the three exercises, always all available (nothing is assigned), with
 * today's done ones from Core and the next sheet / prompt as subtitles. Shared by web and
 * mobile (the reach lesson: never fork data logic per platform). Without provisioned types
 * (staging today) everything shows as to-do and nothing is saved. An exercise begun and left
 * before "Done" only exists on the device (Core records finished attempts), so its "in
 * progress" comes from the exercise progress store, not the sessions.
 */
export function useToday() {
  const date = todayIso();
  const { i18n } = useLingui();
  // Marks included: an exercise a leader marked done today shows as done.
  const sessions = useCountedSessions();
  const workbook = useWorkbook();
  const next = useNextMaterial();
  const minutes = useExerciseMinutes();
  const unfinished = useExerciseProgress();
  const { strongerMemoryTypes } = useConfig();
  const data = useMemo((): DailyPlan | undefined => {
    if (strongerMemoryTypes && sessions.data === undefined) return undefined;
    const done = doneKindsOn(sessions.data ?? [], date);
    // A done row says what was used today; a to-do row says what comes next.
    const todays = attemptsOn(sessions.data ?? [], date);
    const usedToday = (kind: ExerciseKind) =>
      todays.find((attempt) => attempt.kind === kind && attempt.value.state === 'done')
        ?.includes[0];
    const book = workbook.data ?? EMPTY_WORKBOOK;
    const prompt = done.has('writing')
      ? book.prompts.find((candidate) => candidate.lineageUuid === usedToday('writing'))
      : next.prompt;
    const sheet = done.has('math')
      ? book.sheets.find((candidate) => candidate.lineageUuid === usedToday('math'))
      : next.sheet;
    const passage = done.has('reading')
      ? book.passages.find((candidate) => candidate.lineageUuid === usedToday('reading'))
      : next.passage;
    // Own book or paper: the row names what the person typed, not the generic material copy.
    const named = (kind: ExerciseKind) => doneTitleOn(sessions.data ?? [], date, kind);
    const subtitle: Record<ExerciseKind, string> = {
      reading: passage?.title ?? named('reading') ?? i18n._(EXERCISE_COPY.reading.subtitle),
      writing: prompt?.text ?? named('writing') ?? i18n._(EXERCISE_COPY.writing.subtitle),
      math: sheet
        ? i18n._({ ...MATH_SUBTITLE, values: { stage: sheet.stage, sheet: sheet.sheet } })
        : i18n._(EXERCISE_COPY.math.subtitle),
    };
    return dailyPlanSchema.parse({
      date,
      exercises: EXERCISE_ORDER.map((kind) => ({
        kind,
        title: i18n._(EXERCISE_COPY[kind].title),
        subtitle: subtitle[kind],
        minutes,
        status: done.has(kind) ? 'done' : unfinished[kind]?.date === date ? 'in_progress' : 'todo',
      })),
    });
  }, [strongerMemoryTypes, sessions.data, workbook.data, date, i18n, next, minutes, unfinished]);
  return { data, isLoading: sessions.isLoading, error: sessions.error, refetch: sessions.refetch };
}
